'use client';

import {
  FC,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
} from 'react';
import { MassEmailDialogProps } from './MassEmailDialog.types';
import {
  Dialog,
  Textarea,
  Flex,
  Alert,
  Input,
  InputChips,
  Button,
  ButtonGroup,
  SelectItem,
  ErrorBoundary,
} from '@/_components';
import { sendTestMassEmail, sendMassEmail } from '@/_api/admin-conversations';
import { ToastContext } from '@/_contexts/ToastProvider';
import { UserContext } from '@/_contexts/UserProvider';
import { areSetsEqual } from '@/_utils/sets';
import type { User } from '@/_types/models/user';
import { getAllUsers } from '@/_api/v2/admin-users';
import { DEFAULT_V2_USERS_FILTER } from '@/_constants/clients';

export const MassEmailDialog: FC<MassEmailDialogProps> = ({
  isOpen,
  onClose,
}) => {
  const [chipText, setChipText] = useState<string>('');
  const [chips, setChips] = useState<Set<string>>(new Set());

  const [subjectText, setSubjectText] = useState<string>('');
  const [messageText, setMessageText] = useState<string>('');
  const [selectedUsers, setSelectedUsers] = useState<any>([]);
  const [isLoading, setLoading] = useState<boolean>(false);

  const [users, setUsers] = useState<Array<User>>([]);

  const { addToast } = useContext(ToastContext);
  const { token, user, isAdvisor } = useContext(UserContext);

  const onSubjectTextChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSubjectText(event.target.value);
  const onMessageTxtChange = (event: React.ChangeEvent<HTMLTextAreaElement>) =>
    setMessageText(event.target.value);

  const isTestDisabled: boolean = !subjectText || !messageText;
  const isSendDisabled: boolean = isTestDisabled || !selectedUsers?.size;

  const formattedMessage: string = useMemo(
    () => messageText.trim().replace(/\n/g, '<br/>'),
    [messageText],
  );

  const allUserIds: Set<string> = useMemo(
    () => new Set(users.map((user) => `${user.id}`)),
    [users],
  );

  const assignedUserIds: Set<string> = useMemo(
    () =>
      new Set(
        users
          .filter((u) => u.assigned_advisor_id === user?.id)
          .map((u) => `${u.id}`),
      ),
    [users, user?.id],
  );

  const isAllSelected: boolean = areSetsEqual(selectedUsers, allUserIds);
  const isAssignedSelected: boolean = areSetsEqual(
    selectedUsers,
    assignedUserIds,
  );

  const onReset = () => {
    setLoading(false);
    onClose();
    setChipText('');
    setChips(new Set());
    setSubjectText('');
    setMessageText('');
  };

  const onTest = async () => {
    if (isTestDisabled) return;

    try {
      setLoading(true);

      await sendTestMassEmail({
        subject: subjectText,
        body: formattedMessage,
        token,
      });
      addToast('success', 'Test email was sent your email address.');
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      setLoading(false);
    }
  };

  const onSend = async () => {
    if (isSendDisabled) return;

    try {
      setLoading(true);

      await sendMassEmail({
        subject: subjectText,
        body: formattedMessage,
        clientIds: Array.from(selectedUsers),
        token,
      });

      addToast(
        'success',
        'Mass emails have been successfully queued. They should be sent shortly.',
      );
    } catch (error) {
      addToast('error', null, { error });
    } finally {
      onReset();
    }
  };

  const clientResults: Array<SelectItem> = useMemo(
    () =>
      users.map((c) => ({
        name: `${c?.full_name} - ${c?.email}`,
        value: `${c?.id}`,
      })),
    [users],
  );

  const onChipsChange = (items: Set<string>) => {
    setSelectedUsers(items);
  };

  const onChipText = (event: React.ChangeEvent<HTMLInputElement>) =>
    setChipText(event.target.value);

  const onSetSelectUsers = (select: string) => {
    const group = select === 'all' ? allUserIds : assignedUserIds;
    setSelectedUsers(group);
    setChips(group);
  };

  const onClear = () => {
    setSelectedUsers(new Set());
    setChips(new Set());
  };

  const _loadUsers = useCallback(async () => {
    if (!token || !isOpen || !isAdvisor) return;

    try {
      setLoading(true);
      const { users } = await getAllUsers({
        ...DEFAULT_V2_USERS_FILTER,
        rows_per_page: -1,
        token,
      });

      setUsers(users as Array<User>);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [token, isOpen, isAdvisor]);

  useEffect(() => {
    _loadUsers();
  }, [isOpen, _loadUsers]);

  return (
    <ErrorBoundary>
      <Dialog title="Send Mass Email" isOpen={isOpen} onClose={onClose}>
        <Dialog.Content>
          <Flex isColumn={true} gap={8}>
            <Alert type="info">
              You can utilize variables such as{' '}
              <code>&#x7B;client_fname&#x7D;</code>,{' '}
              <code>&#x7B;client_lname&#x7D;</code>,{' '}
              <code>&#x7B;client_full_name&#x7D;</code>,{' '}
              <code>&#x7B;client_preferred_name&#x7D;</code>,{' '}
              <code>&#x7B;client_email&#x7D;</code>,{' '}
              <code>&#x7B;client_phone_number&#x7D;</code> or{' '}
              <code>&#x7B;assigned_firm_user_name&#x7D;</code>.
            </Alert>
            <Flex isColumn={true} gap={4}>
              <InputChips
                autoComplete="off"
                label="Users"
                placeholder="Select Users"
                onChipSelect={onChipsChange}
                isDisabled={isLoading}
                results={clientResults}
                onChange={onChipText}
                setChips={setChips}
                chipNames={clientResults}
                chips={chips}
                value={chipText}
              />
              <ButtonGroup>
                <Button
                  intent="secondary"
                  value="Select All"
                  size="tiny"
                  isDisabled={isLoading}
                  onClick={() => onSetSelectUsers('all')}
                  iconLeft={isAllSelected ? 'check-circle' : 'circle'}
                />
                <Button
                  intent="secondary"
                  value="Select Assigned"
                  size="tiny"
                  isDisabled={isLoading}
                  onClick={() => onSetSelectUsers('assigned')}
                  iconLeft={isAssignedSelected ? 'check-circle' : 'circle'}
                />
                <Button
                  intent="danger-secondary"
                  value="Clear"
                  isDisabled={isLoading}
                  size="tiny"
                  onClick={onClear}
                />
              </ButtonGroup>
            </Flex>
            <Input
              label="Subject"
              placeholder="Type a subject line for the email"
              value={subjectText}
              isDisabled={isLoading}
              onChange={onSubjectTextChange}
            />
            <Textarea
              label="Message"
              value={messageText}
              isDisabled={isLoading}
              rows={5}
              placeholder="Type the body text"
              onChange={onMessageTxtChange}
            />
          </Flex>
        </Dialog.Content>
        <Dialog.Footer>
          <ButtonGroup align="right">
            <Button
              intent="secondary"
              isDisabled={isLoading || isTestDisabled}
              value="Send Test Email To Myself"
              onClick={onTest}
            />
            <Button
              intent="primary"
              value="Send"
              onClick={onSend}
              isLoading={isLoading}
              isDisabled={isSendDisabled || isLoading}
            />
          </ButtonGroup>
        </Dialog.Footer>
      </Dialog>
    </ErrorBoundary>
  );
};
